import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ReactLoading from 'react-loading';
import axios from 'axios';
import { get, uniqBy } from 'lodash-es';
import { Button, Col, FormGroup, Input, Label, Row } from 'reactstrap';

import InlineAlert from '../../../utilities/form-components/InlineAlert';
import {
  errorLoadingCatalogText,
  fileDeleteErrorText,
  offlineErrorText
} from '../../../utilities/StaticText';
import getLotExtension from './getLotExtension';
import updateStepTracker from '../diy-services/updateStepTracker';
import imageUploadErrors from './imageUploadErrors';
import Step4Dropzone from './Step4Dropzone';
import Step4FileList from './Step4FileList';
import parseFileName from './parseFileName';
import { hostServer } from '../../../utilities/UrlPaths';
import step4FilesReducer, {
  initialFilesState,
  createFileEntry,
  ADD_FILES,
  ADMIT_NEXT,
  MARK_COMPLETE,
  MARK_ERROR,
  ABORT,
  UPDATE,
  CLEAR_UPDATED,
  DELETE,
  DELETE_ERRORS
} from './step4FilesReducer';

const Step4 = ({ match, history, location }) => {
  const {
    params: { catalogID, groupID, type }
  } = match;

  const houseID = useSelector(state => state.house.houseID);

  const [{ files }, dispatch] = useReducer(step4FilesReducer, initialFilesState);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [fileUploadFormat, setFileUploadFormat] = useState('lotNumber');
  const [filterOnlyErrors, setFilterOnlyErrors] = useState(false);
  const [offlineError, setOfflineError] = useState(false);
  const [fileDeleteError, setFileDeleteError] = useState(false);
  const [inventoryNumbers, setInventoryNumbers] = useState([]);
  const [lotNumbers, setLotNumbers] = useState(new Set());

  // Always-current view of the file list for cold-path callbacks (delete/update) that must stay
  // referentially stable so they never re-render the uploading tiles.
  const filesRef = useRef(files);
  filesRef.current = files;

  // Load the catalog's lots once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const lotData = await axios(`/cps/app/catalog/${catalogID}/lots`);
      const allLots = get(lotData, 'data.data.lots', []);
      const lots = allLots.filter(lot => lot.status !== 'd');
      if (cancelled) {
        return;
      }
      if (lots.length > 0) {
        setInventoryNumbers(
          lots.map(lot => ({
            invNumber: lot.invNumber,
            lotNumber: lot.lotNumber,
            lotID: lot.lotID,
            lotExt: lot.lotExt.toUpperCase()
          }))
        );
        setLotNumbers(new Set(lots.map(lot => lot.lotNumber + lot.lotExt.toUpperCase())));
        setLoading(false);
      } else {
        setLoading(false);
        setLoadError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [catalogID]);

  // ---- derived counts (never stored) ----
  const numFilesTotal = files.length;
  const numFilesComplete = files.filter(f => f.isComplete).length;
  const isUploading = numFilesTotal > 0 && numFilesComplete < numFilesTotal;

  // Keep the "view items with errors" filter honest: if no errored files remain, clear it.
  useEffect(() => {
    if (filterOnlyErrors && !files.some(f => f.hasError)) {
      setFilterOnlyErrors(false);
    }
  }, [files, filterOnlyErrors]);

  // ---- stable child callbacks (hot path: must not re-render sibling tiles) ----
  const onComplete = useCallback((id, photoID) => dispatch({ type: MARK_COMPLETE, id, photoID }), []);
  const onAbort = useCallback(id => dispatch({ type: ABORT, id }), []);
  const onClearUpdated = useCallback(id => dispatch({ type: CLEAR_UPDATED, id }), []);
  const onError = useCallback(
    (id, code) => dispatch({ type: MARK_ERROR, id, error: imageUploadErrors(code, type) }),
    [type]
  );
  const deleteFilesWithErrors = useCallback(() => dispatch({ type: DELETE_ERRORS }), []);
  const handleErrorFilter = useCallback(() => setFilterOnlyErrors(prev => !prev), []);

  // Resolve a file name to its lot / sequence for the CPS delete call.
  const parseFileByFileName = useCallback(
    fileName => {
      const splitFile = fileName.split('.');
      const lotOrInventoryNumber = parseFileName(splitFile[0]);
      const sequence = splitFile.length >= 3 ? splitFile[1] : '0';

      let lotNumber = '';
      let lotExt = '';
      let lotID = 0;

      if (fileUploadFormat === 'lotNumber') {
        const parseLotName = getLotExtension(lotOrInventoryNumber);
        lotNumber = parseLotName.lotNumber;
        lotExt = parseLotName.lotExt;
        const lotIndex = inventoryNumbers.findIndex(
          image => image.lotNumber === lotNumber && image.lotExt === lotExt
        );
        lotID = inventoryNumbers[lotIndex].lotID;
      } else {
        const lotIndex = inventoryNumbers.findIndex(
          inv => inv.invNumber === lotOrInventoryNumber
        );
        lotNumber = inventoryNumbers[lotIndex].lotNumber;
        lotExt = inventoryNumbers[lotIndex].lotExt;
        lotID = inventoryNumbers[lotIndex].lotID;
      }

      return { lotID, lotExt, lotNumber, sequence };
    },
    [fileUploadFormat, inventoryNumbers]
  );

  const deleteImageFromCps = useCallback(
    async name => {
      const { lotID, sequence } = parseFileByFileName(name);
      await axios.post(
        '/cps/app/image/delete',
        { catalogID, lotID, sequence, imgType: 'lot' },
        { headers: { Accept: 'application/json', 'Content-Type': 'application/json' } }
      );
    },
    [catalogID, parseFileByFileName]
  );

  const onDelete = useCallback(
    async id => {
      const entry = filesRef.current.find(f => f.id === id);
      if (!entry) {
        return;
      }
      // Never uploaded — client-side removal only.
      if (entry.hasError) {
        dispatch({ type: ABORT, id });
        return;
      }
      try {
        await deleteImageFromCps(entry.name);
        dispatch({ type: DELETE, id });
      } catch {
        setFileDeleteError(true);
      }
    },
    [deleteImageFromCps]
  );

  // Edit/rotate save: delete the old image from CPS, then re-arm the entry for a fresh upload.
  const onUpdate = useCallback(
    async (updatedFile, id) => {
      const entry = filesRef.current.find(f => f.id === id);
      if (!entry) {
        return;
      }
      if (entry.hasError) {
        dispatch({ type: ABORT, id });
        return;
      }
      try {
        await deleteImageFromCps(entry.name);
        dispatch({ type: UPDATE, id, file: updatedFile });
      } catch (e) {
        console.error(e); // eslint-disable-line no-console
      }
    },
    [deleteImageFromCps]
  );

  // Treat delimiter variants of the same file name as one (7000.1 / 7000_1 / 7000-1).
  // All entries are added to the reducer immediately so numFilesTotal is stable from the start.
  // ADMIT_NEXT then drip-feeds uploads in batches of 20, waiting while >= 100 are in-flight,
  // to throttle memory usage (thumbnails) on large drops without reducing throughput.
  const handleFileDrop = useCallback(async files_ => {
    if (!navigator.onLine) {
      setOfflineError(true);
      return;
    }

    const entries = uniqBy(files_, f => parseFileName(f.name)).map(f => createFileEntry(f));

    // Add all entries upfront (admitted: false) so the total count is correct immediately.
    setOfflineError(false);
    dispatch({ type: ADD_FILES, entries });

    let toAdmit = entries.length;
    const batchSize = 20;

    while (toAdmit > 0) {
      const inFlight = filesRef.current.filter(f => f.admitted && !f.isComplete).length;

      if (inFlight < 100) {
        const count = Math.min(batchSize, toAdmit);
        toAdmit -= count;
        dispatch({ type: ADMIT_NEXT, count });
        // Yield so React can render and filesRef.current reflects the new admissions.
        // eslint-disable-next-line no-await-in-loop
        await new Promise(resolve => setTimeout(resolve, 0));
      } else {
        // At the concurrency cap, wait for some in-flight uploads to finish before polling again.
        // eslint-disable-next-line no-await-in-loop
        await new Promise(resolve => setTimeout(resolve, 3000));
      }
    }
  }, []);

  const handleFileDropRejected = useCallback(
    files_ => {
      const entries = uniqBy(files_, f => parseFileName(f.name)).map(f =>
        createFileEntry(f, {
          hasError: true,
          isComplete: true,
          error: imageUploadErrors('type', type)
        })
      );
      dispatch({ type: ADD_FILES, entries });
    },
    [type]
  );

  const saveStepFour = () => {
    // Back to Auction Calendar without advancing the step tracker.
    history.push('/my-auctions/current');
  };

  const skipStepFour = async () => {
    const stepNumber = type.toLowerCase() === 'bin' ? 4 : 5;
    await updateStepTracker(catalogID, stepNumber);
    history.push(
      `/session/${type}/${parseInt(groupID, 10)}/${parseInt(catalogID, 10)}/${stepNumber}`
    );
  };

  if (loading) {
    return <ReactLoading className="loading-spinner" type="spin" color="black" />;
  }

  if (loadError) {
    return <InlineAlert baseText={errorLoadingCatalogText} />;
  }

  const isBin = type.toLowerCase() === 'bin';
  const isLinkedFromSummary = get(location, 'state.linkedFrom', null);
  const isEditingPage = !isBin && isLinkedFromSummary === 'step6';
  const isEditingPageBin = isBin && isLinkedFromSummary === 'step5';
  const isEditingSession = isEditingPage || isEditingPageBin;

  const conventionText = fileUploadFormat === 'lotNumber' ? 'Item' : 'Inventory';
  const conventionNumber = fileUploadFormat === 'lotNumber' ? '1' : 'ABC123';

  return (
    <div className="mt-4">
      <Row>
        <Col xs={8}>
          <strong>Upload Images</strong>
          <p className="mb-2">
            1. Drag &amp; drop images from your computer in the uploader box below (or click the
            &quot;choose a file(s) from your computer&quot; link to find your file)
          </p>
          <p className="mb-2">2. Correct Errors if shown.</p>
          <p>
            Note: any images that contain errors will not be uploaded or saved after clicking
            &quot;Continue&quot;.
          </p>

          <strong>Select Your File Format</strong>
          <p>You can choose to upload images by:</p>
          <FormGroup check>
            <Label for="lotNumber" check>
              <Input
                id="lotNumber"
                type="radio"
                name="imageFormat"
                disabled={files.length > 0}
                checked={fileUploadFormat === 'lotNumber'}
                onChange={e => setFileUploadFormat(e.target.id)}
              />
              {`${isBin ? 'Item' : 'Lot'} Number`}
            </Label>
          </FormGroup>
          <FormGroup check className="my-2">
            <Label for="inventoryNumber" check>
              <Input
                id="inventoryNumber"
                type="radio"
                name="imageFormat"
                disabled={files.length > 0}
                checked={fileUploadFormat === 'inventoryNumber'}
                onChange={e => setFileUploadFormat(e.target.id)}
              />
              Inventory Number
            </Label>
          </FormGroup>

          {offlineError && (
            <div className="mt-3">
              <InlineAlert baseText={offlineErrorText} showPhones={false} />
            </div>
          )}

          <Step4Dropzone
            acceptedFileList=".jpg,.jpeg,.png"
            handleFileDrop={handleFileDrop}
            handleFileDropRejected={handleFileDropRejected}
          />
        </Col>

        <Col xs={4}>
          <strong>Image Requirements</strong>
          <p>Images must end in .JPG, .JPEG &amp; .PNG (Do NOT use .GIF)</p>
          <strong>Naming convention for {conventionText.toLowerCase()} number match:</strong>
          <p className="mb-0">Primary Photo:</p>
          <p className="mb-0">{conventionText}.FileType</p>
          <p>ex: {conventionNumber}.1.jpeg</p>

          <p className="mb-0">Additional Photos for Item:</p>
          <p className="mb-0">{conventionText}.SequenceNumber.FileType</p>
          <p>ex: {conventionNumber}.1.jpeg</p>

          {fileUploadFormat === 'lotNumber' && (
            <>
              <p className="mb-0">Items with extensions:</p>
              <p className="mb-0">{conventionText}Extension.SequenceNumber.FileType</p>
              <p>ex: {conventionNumber}A.2.jpeg</p>
            </>
          )}

          <Row>
            <Col>
              <strong>Max. Size</strong>
            </Col>
          </Row>
          <Row>
            <Col>
              <p>up to 10MB</p>
            </Col>
          </Row>

          <p>
            Use our{' '}
            <a
              rel="noopener noreferrer"
              target="_blank"
              href={`${hostServer}/inv/help-center/resource/catalog/preparing-images/`}
            >
              upload tip sheet
            </a>{' '}
            for help with uploading images.
          </p>
        </Col>
      </Row>

      {fileDeleteError && (
        <div className="mt-3">
          <InlineAlert baseText={fileDeleteErrorText} />
        </div>
      )}

      <Step4FileList
        entries={files}
        numFilesTotal={numFilesTotal}
        numFilesComplete={numFilesComplete}
        catalogID={catalogID}
        houseID={houseID}
        type={type}
        fileUploadFormat={fileUploadFormat}
        filterOnlyErrors={filterOnlyErrors}
        lotNumbers={lotNumbers}
        inventoryNumbers={inventoryNumbers}
        handleErrorFilter={handleErrorFilter}
        deleteFilesWithErrors={deleteFilesWithErrors}
        onComplete={onComplete}
        onError={onError}
        onAbort={onAbort}
        onDelete={onDelete}
        onUpdate={onUpdate}
        onClearUpdated={onClearUpdated}
      />

      <div className="mt-4">
        <Button
          className={isUploading ? 'cursor-not-allowed' : ''}
          role="link"
          color="primary"
          disabled={isUploading}
          onClick={() => skipStepFour()}
        >
          {numFilesTotal === 0 ? 'Skip & Continue' : 'Continue'}
        </Button>
        {!isUploading && !isEditingSession && (
          <Button className="ml-5" role="link" color="link" onClick={() => saveStepFour()}>
            Save &amp; Exit
          </Button>
        )}
        {!isUploading && isEditingSession && (
          <Link
            className="ml-5"
            aria-label="Cancel & Return to Summary"
            to={`/session/${type}/${parseInt(groupID, 10)}/${parseInt(catalogID, 10)}/${
              isBin ? '5' : '6'
            }`}
          >
            Cancel &amp; Return to Summary
          </Link>
        )}
      </div>
    </div>
  );
};

export default withRouter(Step4);
